import { handle } from 'redux-pack';

import { Action } from '../../../types/actions';
import type { IWatermarkSet } from '../../../types/watermarks.types';
import {
	LOAD_CHANNEL_WATERMARKS,
	LOAD_WATERMARK,
	CREATE_WATERMARK,
	UPDATE_WATERMARK,
	DELETE_WATERMARK,
	REMOVE_WATERMARK,
	SET_CURRENT_WATERMARK,
	CLEAR_CURRENT_WATERMARK,
	RESET_WATERMARKS_REDUCER,
} from '../../actions/admin/watermarks';

export interface WatermarksState {
	watermarks: IWatermarkSet[];
	currentWatermark: IWatermarkSet | null;
	loading: boolean;
	error: string | null;
}

const initialState: WatermarksState = {
	watermarks: [],
	currentWatermark: null,
	loading: false,
	error: null,
};

export default function WatermarksReducer(
	state: WatermarksState = initialState,
	action: Action
): WatermarksState {
	switch (action.type) {
		case LOAD_CHANNEL_WATERMARKS: {
			return handle(state, action, {
				start: (state) => ({ ...state, loading: true }),
				failure: (state) => ({ ...state, error: 'Unable to load watermarks.' }),
				success: (state) => ({ ...state, error: null, watermarks: action.payload }),
				finish: (state) => ({ ...state, loading: false }),
			});
		}
		case LOAD_WATERMARK: {
			return handle(state, action, {
				start: (state) => ({ ...state, loading: true }),
				failure: (state) => ({ ...state, error: 'Unable to load watermark.' }),
				success: (state) => ({ ...state, error: null, currentWatermark: action.payload }),
				finish: (state) => ({ ...state, loading: false }),
			});
		}
		case CREATE_WATERMARK: {
			return handle(state, action, {
				start: (state) => ({ ...state, loading: true }),
				failure: (state) => ({ ...state, error: 'Unable to create watermark.' }),
				success: (state) => ({
					...state,
					error: null,
					watermarks: [...state.watermarks, action.payload],
					currentWatermark: action.payload,
				}),
				finish: (state) => ({ ...state, loading: false }),
			});
		}
		case UPDATE_WATERMARK: {
			return handle(state, action, {
				start: (state) => ({ ...state, loading: true }),
				failure: (state) => ({ ...state, error: 'Unable to update watermark.' }),
				success: (state) => ({
					...state,
					error: null,
					watermarks: state.watermarks.map((w) =>
						w.uuid === action.payload.uuid ? action.payload : w
					),
					currentWatermark:
						state.currentWatermark?.uuid === action.payload.uuid
							? action.payload
							: state.currentWatermark,
				}),
				finish: (state) => ({ ...state, loading: false }),
			});
		}
		case DELETE_WATERMARK: {
			return handle(state, action, {
				start: (state) => ({ ...state, loading: true }),
				failure: (state) => ({ ...state, error: 'Unable to delete watermark.' }),
				success: (state) => ({
					...state,
					error: null,
					watermarks: state.watermarks.filter((w) => w.uuid !== action.meta?.uuid),
					currentWatermark:
						state.currentWatermark?.uuid === action.meta?.uuid
							? null
							: state.currentWatermark,
				}),
				finish: (state) => ({ ...state, loading: false }),
			});
		}
		case REMOVE_WATERMARK: {
			return {
				...state,
				watermarks: state.watermarks.filter((w) => w.uuid !== action.payload),
				currentWatermark:
					state.currentWatermark?.uuid === action.payload ? null : state.currentWatermark,
			};
		}
		case SET_CURRENT_WATERMARK: {
			return { ...state, currentWatermark: action.payload };
		}
		case CLEAR_CURRENT_WATERMARK: {
			return { ...state, currentWatermark: null };
		}
		case RESET_WATERMARKS_REDUCER: {
			return initialState;
		}
		default:
			return state;
	}
}
